import { useCallback, useEffect } from 'react';

type UseTriggerCallbackOnIntervalProps<T> = {
    listToTrigger: T[] | readonly T[];
    callback: (item: T, isLastTrigger: boolean) => void | (() => void);
    intervalMs: number;
};

export const useTriggerCallbackOnInterval = <T>({
    listToTrigger,
    callback,
    intervalMs,
}: UseTriggerCallbackOnIntervalProps<T>) => {
    const recursiveCallback = useCallback(
        (index: number = 0) => {
            if (index >= listToTrigger.length) return undefined;

            const nextIndex = index + 1;
            const isLastTrigger = nextIndex >= listToTrigger.length;
            callback(listToTrigger[index], isLastTrigger);

            const timeoutId = setTimeout(() => recursiveCallback(nextIndex), intervalMs);

            return timeoutId;
        },
        [callback, intervalMs, listToTrigger],
    );

    useEffect(() => {
        // This timeout is necessary so that places that use this code can watch for changes to something
        // and reset the listToTrigger. For an example, see how setVisibleMessages([]) is called in IntroMessages.tsx
        const timeoutId = setTimeout(recursiveCallback, 0);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [recursiveCallback]);
};
